import { CrudProperty, ICrudProperty } from "../CrudProperty";

import dayjs, { OpUnitType } from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import utc from "dayjs/plugin/utc";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { CrudModel } from "../CrudModel";
dayjs.extend(relativeTime);
dayjs.extend(utc);
dayjs.extend(customParseFormat);

export interface IDateTimeProperty extends ICrudProperty {
  displayFormat?: string;
  displayRelative?: boolean;
}
export class DateTimeProperty extends CrudProperty {
  public displayFormat: string = "M/D/YYYY h:mm A";
  protected displayRelative = false;

  constructor(opts: IDateTimeProperty, model: CrudModel) {
    super(opts, model);

    // defaults
    if (typeof opts.sortable === "undefined") this.sortable = true;

    if (typeof opts.displayFormat !== "undefined")
      this.displayFormat = opts.displayFormat;

    if (typeof opts.displayRelative !== "undefined")
      this.displayRelative = opts.displayRelative;
  }

  public get stringValue(): string {
    if (!this.value) return super.stringValue;

    if (this.displayRelative) return dayjs(this.value).fromNow();

    return dayjs(this.value).format(this.displayFormat);
  }

  public coerceValue(val: unknown): any {
    if (!val) return null;

    // A stored date whose components are out of range (a MySQL zero-date, or a
    // legacy value like 2024-02-30) parses leniently and silently rolls over
    // into a different, valid-looking date. Reject it rather than corrupt it.
    if (DateTimeProperty.hasOutOfRangeDate(val)) return null;

    // An unparseable input yields an Invalid Date that is truthy, so
    // serializedValueGuarded would throw on toISOString. Reject it at the funnel.
    const parsed = dayjs(val as string);
    if (!parsed.isValid()) return null;

    return super.coerceValue(parsed.toDate());
  }

  // dayjs parses out-of-range date components leniently, rolling e.g.
  // '2024-02-30' forward to 2024-03-01 and MySQL zero-dates back into the
  // 1800s, so a stored-but-invalid date sails in as a valid-but-wrong one.
  // Strict-validate the leading YYYY-MM-DD of any string input to catch it;
  // Date/dayjs inputs (round-trips, now()) carry no bad prefix and pass through.
  protected static hasOutOfRangeDate(val: unknown): boolean {
    if (typeof val !== "string") return false;
    const datePrefix = val.match(/^\d{4}-\d{2}-\d{2}/);
    return datePrefix
      ? !dayjs.utc(datePrefix[0], "YYYY-MM-DD", true).isValid()
      : false;
  }

  protected static compareResolution: OpUnitType = "second";
  public compareValues(a: any, b: any): number {
    const aCoerced = this.coerceValue(a);
    const bCoerced = this.coerceValue(b);

    if (
      a === b ||
      dayjs(aCoerced).isSame(
        dayjs(bCoerced),
        (this._classRef as typeof DateTimeProperty).compareResolution
      )
    )
      return 0;
    return super.compareValues(aCoerced, bCoerced);
  }

  public serializedValueGuarded(visited = new WeakSet<any>()): any {
    return this.value ? this.value.toISOString() : this.value;
  }

  public static now() {
    return dayjs.utc().toDate();
  }
}
